/**
 * HUD occupancy contract — the read-only geometry the render layer queries so it can
 * keep world-space marks out of the chrome.
 *
 * Two consumers, both R6:
 *
 * - **D8, damage numbers.** §11.9's render order puts numbers directly under the UI,
 *   which means a number spawned near the top of the screen lands *on* the run timer.
 *   The HUD publishes the rectangles it actually occupies; the number spawner clamps
 *   or suppresses inside them.
 * - **D14, off-screen threat chevrons.** Chevrons live in a band hugging the viewport
 *   edge. The HUD guarantees it never draws inside `edgeBand` px of the left, right
 *   and bottom edges. AMD-48 deleted the dash button, which was the single documented
 *   exception, so the guarantee holds unconditionally and the chevron layer has the
 *   whole band; the top band is published as `top` so it can push down past it.
 *
 * Everything here is CSS pixels in viewport coordinates (origin top-left), which is
 * the same space `canvas.getBoundingClientRect()` reports. Nothing in this module
 * allocates after construction: `Hud` owns one `MutableZones` and hands out a frozen
 * view of it, so a per-frame `ui.hudZones` read costs a property fetch.
 */

export interface HudRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface HudReservedZones {
  /**
   * Every rectangle the HUD currently paints into, in paint order. Empty entries are
   * collapsed to zero-size rects rather than removed, so the array identity is stable.
   */
  readonly rects: readonly HudRect[];
  /** Union of the top cluster (vitals + XP + build + boss bar). Height 0 when hidden. */
  readonly top: HudRect;
  /** Guaranteed-clear band, in px, along the left, right and bottom viewport edges. */
  readonly edgeBand: number;
  /** The rectangle no HUD element paints into — safe for damage numbers unclamped. */
  readonly play: HudRect;
  /** Viewport the rects were measured against. */
  readonly viewport: HudRect;
  /** Bumps on every re-measure; consumers can cache derived data against it. */
  readonly revision: number;
}

/** Mutable backing store. Only `Hud` writes to it. */
export interface MutableRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MutableZones {
  rects: MutableRect[];
  top: MutableRect;
  edgeBand: number;
  play: MutableRect;
  viewport: MutableRect;
  revision: number;
}

export function rect(): MutableRect {
  return { x: 0, y: 0, w: 0, h: 0 };
}

export function setRect(r: MutableRect, x: number, y: number, w: number, h: number): void {
  r.x = x;
  r.y = y;
  r.w = w;
  r.h = h;
}

export function clearRect(r: MutableRect): void {
  r.x = 0;
  r.y = 0;
  r.w = 0;
  r.h = 0;
}

/** Grows `into` to cover `r`. A zero-area `r` is ignored. */
export function unionInto(into: MutableRect, r: MutableRect): void {
  if (r.w <= 0 || r.h <= 0) return;
  if (into.w <= 0 || into.h <= 0) {
    setRect(into, r.x, r.y, r.w, r.h);
    return;
  }
  const x0 = Math.min(into.x, r.x);
  const y0 = Math.min(into.y, r.y);
  const x1 = Math.max(into.x + into.w, r.x + r.w);
  const y1 = Math.max(into.y + into.h, r.y + r.h);
  setRect(into, x0, y0, x1 - x0, y1 - y0);
}

/** True when the point sits inside any occupied HUD rectangle. Allocation-free. */
export function hudOccludes(z: HudReservedZones, x: number, y: number): boolean {
  const rs = z.rects;
  for (let i = 0; i < rs.length; i++) {
    const r = rs[i];
    if (r.w <= 0 || r.h <= 0) continue;
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return true;
  }
  return false;
}

/**
 * Vertical offset that moves a mark at `y` clear of the top band, or 0 when it is
 * already clear. The damage-number spawner adds this to its own rise offset (§11.11)
 * instead of drawing over the timer.
 */
export function pushBelowTop(z: HudReservedZones, x: number, y: number, margin = 4): number {
  const t = z.top;
  if (t.w <= 0 || t.h <= 0) return 0;
  if (x < t.x || x > t.x + t.w) return 0;
  const bottom = t.y + t.h + margin;
  return y < bottom ? bottom - y : 0;
}
